import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Calendar, MapPin, Palette, Copy, UserCheck, ArrowLeft, Mail } from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Loading from '../components/common/Loading';
import { eventAPI } from '../api/endpoints';
import { useToastContext } from '../context/ToastContext';

import { useEvent } from '../context/EventContext';

const EventForm = ({ eventId }: { eventId?: string }) => {
  const { setSelectedEvent } = useEvent();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const isEditing = eventId && eventId !== 'new';
  const id = isEditing ? eventId : null;

  const { data: eventData, isLoading: isLoadingEvent } = useQuery({
    queryKey: ['events', id],
    queryFn: async () => {
      const response = await eventAPI.getById(id);
      return response.data;
    },
    enabled: isEditing,
  });
  
  const event = eventData?.event || null;

  const createMutation = useMutation({
    mutationFn: (data: any) => eventAPI.create(data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['events-with-counts'] });
      toast.success('Event created successfully!');
      if (response.data && response.data.event) {
        setSelectedEvent(response.data.event.id);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create event');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: any }) => eventAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['events-with-counts'] });
      toast.success('Event updated successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update event');
    },
  });

  const loading = createMutation.isPending || updateMutation.isPending || isLoadingEvent;

  const [formData, setFormData] = useState<any>({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    location: '',
    is_active: true,
    allow_duplicates: false,
    registration_starts_at: '',
    registration_ends_at: '',
    primary_color: '#3B82F6',
    secondary_color: '#10B981',
    background_color: '#FFFFFF',
    text_color: '#000000',
    badge_layout: 'standard',
    show_qr_code: true,
    show_logo: true,
    show_registration_number: false,
    registration_number_format: 'REG-{id}',
    copy_fields_from_event_id: '',
    require_email: true,
    require_organization: false,
    require_position: false,
    email_notification_setting: 'disabled',
  });

  // Fetch default event for copying fields
  const { data: defaultEventData } = useQuery({
    queryKey: ['defaultEvent'],
    queryFn: async () => {
      const response = await eventAPI.getDefault();
      return response.data;
    },
    enabled: !event, // Only fetch when creating new event
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form when editing or set default event for copying
  useEffect(() => {
    if (event) {
      setFormData({
        name: event.name || '',
        description: event.description || '',
        start_date: event.start_date ? event.start_date.slice(0, 16) : '',
        end_date: event.end_date ? event.end_date.slice(0, 16) : '',
        registration_starts_at: event.registration_starts_at ? event.registration_starts_at.slice(0, 16) : '',
        registration_ends_at: event.registration_ends_at ? event.registration_ends_at.slice(0, 16) : '',
        location: event.location || '',
        is_active: event.is_active ?? true,
        allow_duplicates: event.allow_duplicates ?? false,
        primary_color: event.primary_color || '#3B82F6',
        secondary_color: event.secondary_color || '#10B981',
        background_color: event.background_color || '#FFFFFF',
        text_color: event.text_color || '#000000',
        badge_layout: event.badge_layout || 'standard',
        show_qr_code: event.show_qr_code ?? true,
        show_logo: event.show_logo ?? true,
        show_registration_number: event.show_registration_number ?? false,
        registration_number_format: event.registration_number_format || 'REG-{id}',
        copy_fields_from_event_id: '',
        require_email: event.require_email ?? true,
        require_organization: event.require_organization ?? false,
        require_position: event.require_position ?? false,
        email_notification_setting: event.email_notification_setting || 'disabled',
      });
    } else {
      // Reset form for new event and auto-select default event for copying fields
      setFormData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        registration_starts_at: '',
        registration_ends_at: '',
        location: '',
        is_active: true,
        allow_duplicates: false,
        primary_color: '#3B82F6',
        secondary_color: '#10B981',
        background_color: '#FFFFFF',
        text_color: '#000000',
        badge_layout: 'standard',
        show_qr_code: true,
        show_logo: true,
        show_registration_number: false,
        registration_number_format: 'REG-{id}',
        copy_fields_from_event_id: defaultEventData?.data?.id || '',
        require_email: true,
        require_organization: false,
        require_position: false,
        email_notification_setting: 'disabled',
      });
    }
    setErrors({});
  }, [event, defaultEventData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Event name is required';
    }

    if (formData.end_date && formData.start_date) {
      if (new Date(formData.end_date) < new Date(formData.start_date)) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Clean up empty strings to null for backend validation
    const cleanedData = {
      ...formData,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      registration_starts_at: formData.registration_starts_at || null,
      registration_ends_at: formData.registration_ends_at || null,
      location: formData.location || null,
      description: formData.description || null,
      copy_fields_from_event_id: formData.copy_fields_from_event_id || null
    };

    if (event) {
      updateMutation.mutate({ id: event.id, data: cleanedData });
    } else {
      createMutation.mutate(cleanedData);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Basic Information
              </h4>

              <Input
                label="Event Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                error={errors.name}
                required
                placeholder="e.g., Annual Tech Conference 2025"
                disabled={loading}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Brief description of the event..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={loading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Event Start (Date & Time)"
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                  error={errors.start_date}
                  disabled={loading}
                />

                <Input
                  label="Event End (Date & Time)"
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => handleChange('end_date', e.target.value)}
                  error={errors.end_date}
                  disabled={loading}
                />
              </div>

              <Input
                label="Location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="e.g., Convention Center, New York"
                icon={MapPin}
                disabled={loading}
              />

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  disabled={loading}
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Event is active (visible to users)
                </label>
              </div>

              {/* Copy Fields Info - Only show when creating new event */}
              {!event && formData.copy_fields_from_event_id && defaultEventData?.data && (
                <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <Copy className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">
                      Custom fields will be copied
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      Registration fields from "{defaultEventData.data.name}" (default event) will be automatically copied to this new event.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Registration Window */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <UserCheck className="h-4 w-4" />
                Registration Settings
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Registration Opens</label>
                  <input
                    type="datetime-local"
                    value={formData.registration_starts_at}
                    onChange={(e) => handleChange('registration_starts_at', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-400 mt-1">Leave blank to open immediately</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Registration Closes</label>
                  <input
                    type="datetime-local"
                    value={formData.registration_ends_at}
                    onChange={(e) => handleChange('registration_ends_at', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-400 mt-1">Leave blank to never auto-close</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
                <input
                  type="checkbox"
                  id="allow_duplicates"
                  checked={formData.allow_duplicates}
                  onChange={(e) => handleChange('allow_duplicates', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
                  disabled={loading}
                />
                <div>
                  <label htmlFor="allow_duplicates" className="block text-sm font-medium text-gray-700 cursor-pointer">
                    Allow Duplicate Registrations
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When enabled, the same email or phone number can be used to register multiple times for this event.
                  </p>
                </div>
              </div>
            </div>

            {/* Badge Customization */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Palette className="h-4 w-4" />
                Badge Customization
              </h4>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      className="h-10 w-full rounded border border-gray-300 cursor-pointer"
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formData.primary_color}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secondary Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.secondary_color}
                      onChange={(e) => handleChange('secondary_color', e.target.value)}
                      className="h-10 w-full rounded border border-gray-300 cursor-pointer"
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formData.secondary_color}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.background_color}
                      onChange={(e) => handleChange('background_color', e.target.value)}
                      className="h-10 w-full rounded border border-gray-300 cursor-pointer"
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formData.background_color}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Text Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.text_color}
                      onChange={(e) => handleChange('text_color', e.target.value)}
                      className="h-10 w-full rounded border border-gray-300 cursor-pointer"
                      disabled={loading}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formData.text_color}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Badge Layout
                </label>
                <select
                  value={formData.badge_layout}
                  onChange={(e) => handleChange('badge_layout', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={loading}
                >
                  <option value="standard">Standard</option>
                  <option value="modern">Modern</option>
                  <option value="minimal">Minimal</option>
                  <option value="corporate">Corporate</option>
                </select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="show_qr_code"
                    checked={formData.show_qr_code}
                    onChange={(e) => handleChange('show_qr_code', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    disabled={loading}
                  />
                  <label htmlFor="show_qr_code" className="ml-2 block text-sm text-gray-700">
                    Show QR code on badges
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="show_logo"
                    checked={formData.show_logo}
                    onChange={(e) => handleChange('show_logo', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    disabled={loading}
                  />
                  <label htmlFor="show_logo" className="ml-2 block text-sm text-gray-700">
                    Show event logo on badges
                  </label>
                </div>
              </div>
            </div>

            {/* Registration Settings */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <UserCheck className="h-4 w-4" />
                Registration Settings
              </h4>

              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="show_registration_number"
                    checked={formData.show_registration_number}
                    onChange={(e) => handleChange('show_registration_number', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    disabled={loading}
                  />
                  <label htmlFor="show_registration_number" className="ml-2 block text-sm text-gray-700">
                    Show registration number on badges
                  </label>
                </div>

                {formData.show_registration_number && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Registration Number Format
                    </label>
                    <select
                      value={formData.registration_number_format}
                      onChange={(e) => handleChange('registration_number_format', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      disabled={loading}
                    >
                      <option value="REG-{id}">REG-{`{id}`} (e.g., REG-0001, REG-1000)</option>
                      <option value="REG-{year}-{id}">REG-{`{year}`}-{`{id}`} (e.g., REG-2024-0001)</option>
                      <option value="REG-{year}{month}-{id}">REG-{`{year}`}{`{month}`}-{`{id}`} (e.g., REG-202410-0001)</option>
                      <option value="REG-{alpha}{id}">REG-{`{alpha}`}{`{id}`} (e.g., REG-A001, REG-B001)</option>
                      <option value="{year}-{sequential}">Custom: {`{year}`}-{`{sequential}`} (e.g., 2024-001)</option>
                      <option value="{year}{month}-{sequential}">Custom: {`{year}`}{`{month}`}-{`{sequential}`} (e.g., 202410-0001)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.registration_number_format === 'REG-{id}' && 'Standard format with padded numbers'}
                      {formData.registration_number_format === 'REG-{year}-{id}' && 'Includes current year in format'}
                      {formData.registration_number_format === 'REG-{year}{month}-{id}' && 'Includes year and month for large events'}
                      {formData.registration_number_format === 'REG-{alpha}{id}' && 'Uses letters for very large events (A001, B001, etc.)'}
                      {(formData.registration_number_format === '{year}-{sequential}' || formData.registration_number_format === '{year}{month}-{sequential}') && 'Custom date-based format'}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="require_email"
                      checked={formData.require_email}
                      onChange={(e) => handleChange('require_email', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      disabled={loading}
                    />
                    <label htmlFor="require_email" className="ml-2 block text-sm text-gray-700">
                      Require Email
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="require_organization"
                      checked={formData.require_organization}
                      onChange={(e) => handleChange('require_organization', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      disabled={loading}
                    />
                    <label htmlFor="require_organization" className="ml-2 block text-sm text-gray-700">
                      Require Organization
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="require_position"
                      checked={formData.require_position}
                      onChange={(e) => handleChange('require_position', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      disabled={loading}
                    />
                    <label htmlFor="require_position" className="ml-2 block text-sm text-gray-700">
                      Require Position/Title
                    </label>
                  </div>
                </div>

                <div className="mt-6 border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Notifications
                  </label>
                  <p className="text-xs text-gray-500 mb-3">Configure if attendees receive confirmation emails upon registration.</p>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="email_notification_setting"
                        value="always"
                        checked={formData.email_notification_setting === 'always'}
                        onChange={(e) => handleChange('email_notification_setting', e.target.value)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        disabled={loading}
                      />
                      <span className="ml-2 text-sm text-gray-700">Always send (Default)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="email_notification_setting"
                        value="opt_in"
                        checked={formData.email_notification_setting === 'opt_in'}
                        onChange={(e) => handleChange('email_notification_setting', e.target.value)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        disabled={loading}
                      />
                      <span className="ml-2 text-sm text-gray-700">User Opt-in (Adds checkbox to form)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="email_notification_setting"
                        value="disabled"
                        checked={formData.email_notification_setting === 'disabled'}
                        onChange={(e) => handleChange('email_notification_setting', e.target.value)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                        disabled={loading}
                      />
                      <span className="ml-2 text-sm text-gray-700">Disabled (Never send)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t sticky bottom-0 bg-white">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/events')}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                loading={loading}
              >
                {event ? 'Update Event' : 'Create Event'}
              </Button>
            </div>
          </form>
      </div>
    </div>
  );
};

export default EventForm;
